import React from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Copy,
    Save,
    ExternalLink,
    Trash2,
    Folder,
    Edit3,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api_endpoints } from "@/lib/api";
import {
    fmtCurrency,
    fmtMileage,
    fmtDate,
    verdictColor,
} from "@/lib/format";
import {
    VerdictBadge,
    Btn,
    Card,
    Eyebrow,
    TextInput,
    PageHeader,
} from "@/components/app/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AnalysisReport({ shared }) {
    const { t } = useTranslation();
    const { id, token } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const sharedToken = shared ? token : null;
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [collections, setCollections] = React.useState([]);
    const [editingNote, setEditingNote] = React.useState(false);
    const [noteDraft, setNoteDraft] = React.useState("");
    const [expandedGroups, setExpandedGroups] = React.useState([]);
    const [safetyTab, setSafetyTab] = React.useState("recalls");
    const noteRef = React.useRef(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const d = shared
                ? await api_endpoints.sharedAnalysis(sharedToken, id)
                : await api_endpoints.historyOne(id);
            setData(d);
            setNoteDraft(d.notes || "");
        } catch (e) {
            setError(e.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, [id, shared, sharedToken]);

    React.useEffect(() => {
        load();
    }, [load]);

    React.useEffect(() => {
        if (!shared)
            api_endpoints
                .collections()
                .then(setCollections)
                .catch(() => {});
    }, [shared]);

    async function saveNote() {
        try {
            await api_endpoints.historyUpdate(id, { notes: noteDraft });
            setData((d) => ({ ...d, notes: noteDraft }));
            setEditingNote(false);
            toast.success(t("report.noteSaved"));
        } catch (e) {
            toast.error(e.message);
        }
    }

    async function changeCollection(collectionId) {
        try {
            await api_endpoints.historyUpdate(id, { collectionId });
            const c = collections.find((x) => x.id === collectionId);
            setData((d) => ({
                ...d,
                collectionId,
                collectionName: c?.name || "",
            }));
            toast.success("Moved to " + (c?.name || "collection"));
        } catch (e) {
            toast.error(e.message);
        }
    }

    function copyList(label, list) {
        navigator.clipboard.writeText(list.join("\n"));
        toast.success(`${label} copied to clipboard`);
    }

    if (loading)
        return (
            <div className="p-10 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                {t("report.loading")}
            </div>
        );
    if (error || !data)
        return (
            <div className="p-10">
                <Card className="p-6">
                    <div className="font-display text-2xl uppercase">{t("report.notFound")}</div>
                    <p className="mt-2 text-sm text-zinc-600">{error}</p>
                    <Btn
                        href="/app/dashboard"
                        variant="outline"
                        icon={ArrowLeft}
                        className="mt-4"
                    >
                        {t("report.backToDashboard")}
                    </Btn>
                </Card>
            </div>
        );

    const r = data.detailedReport || {};
    const mp = r.marketPriceAnalysis;
    const urgencyOrder = { upcoming: 0, "long-term": 1, routine: 2 };
    const maint = [...(r.maintenanceForecast || [])].sort(
        (a, b) => (urgencyOrder[a.urgency] ?? 99) - (urgencyOrder[b.urgency] ?? 99)
    );

    const showSafety = (data.nhtsaRecalls ?? 0) > 0 || (data.nhtsaComplaints ?? 0) > 0 || (data.nhtsaCrashes ?? 0) > 0 || (data.nhtsaRecallSummaries?.length ?? 0) > 0;
    const showOdometer = data.odometerRecords?.length > 0 || data.odometerFraudDetected || data.odometerFraudDetails || data.odometerVehicleName;
    const isConvertedSpecs = data.techSpecs?.specsProvenance === "factoryEngineForConvertedVehicle";
    const hasValidSharedConfidence = data.techSpecs?.matchConfidence === "shared"
        && Number.isInteger(data.techSpecs.compatibleCandidateCount)
        && data.techSpecs.compatibleCandidateCount >= 2
        && data.techSpecs.compatibleCandidateCount <= 4;
    const shouldRenderTechSpecs = data.techSpecs != null
        && (data.techSpecs.matchConfidence !== "shared" || hasValidSharedConfidence);
    const hasMatchCaveat = data.techSpecs?.matchConfidence === "inferred";
    const summarySpecs = data.techSpecs ? [
        data.techSpecs.power && {
            key: "power",
            category: "engine",
            label: t("report.specPower"),
            value: `${data.techSpecs.power}${data.techSpecs.torque ? ` / ${data.techSpecs.torque}` : ""}`,
            comparisonValues: [data.techSpecs.power, data.techSpecs.torque].filter(Boolean),
            comparisonKeys: ["power", "torque"],
            comparisonLabels: [t("report.specPower"), t("report.specTorque")],
        },
        data.techSpecs.acceleration0To100 && {
            key: "acceleration",
            category: "performance",
            label: t("report.specAcceleration"),
            value: data.techSpecs.acceleration0To100,
        },
        data.techSpecs.topSpeed && {
            key: "topSpeed",
            category: "performance",
            label: t("report.specTopSpeed"),
            value: data.techSpecs.topSpeed,
        },
        data.techSpecs.fuelConsumption && {
            key: "fuelConsumption",
            category: "fuelEmissions",
            label: t("report.specFuelConsumption"),
            value: data.techSpecs.fuelConsumption,
        },
        data.techSpecs.co2Emissions && {
            key: "co2Emissions",
            category: "fuelEmissions",
            label: t("report.specCo2"),
            value: data.techSpecs.co2Emissions,
        },
        data.techSpecs.engineDisplacement && {
            key: "engine",
            category: "engine",
            label: t("report.specEngine"),
            value: `${data.techSpecs.engineDisplacement}${data.techSpecs.cylinders ? ` · ${data.techSpecs.cylinders}` : ""}`,
            comparisonValues: [data.techSpecs.engineDisplacement, data.techSpecs.cylinders].filter(Boolean),
            comparisonKeys: ["engineDisplacement", "cylinders"],
        },
        data.techSpecs.gearbox && {
            key: "gearbox",
            category: "powertrain",
            label: t("report.specGearbox"),
            value: data.techSpecs.gearbox,
        },
        data.techSpecs.drivetrain && {
            key: "drivetrain",
            category: "powertrain",
            label: t("report.specDrivetrain"),
            value: data.techSpecs.drivetrain,
        },
        data.techSpecs.bodyType && {
            key: "bodyType",
            category: "general",
            label: t("report.specBodyType"),
            value: data.techSpecs.bodyType,
        },
        data.techSpecs.curbWeight && {
            key: "curbWeight",
            category: "weightCapacity",
            label: t("report.specWeight"),
            value: data.techSpecs.curbWeight,
        },
        data.techSpecs.trunkVolume && {
            key: "trunkVolume",
            category: "weightCapacity",
            label: t("report.specTrunk"),
            value: data.techSpecs.trunkVolume,
        },
        (data.techSpecs.length || data.techSpecs.width) && {
            key: "dimensions",
            category: "dimensions",
            label: t("report.specDimensions"),
            value: [data.techSpecs.length, data.techSpecs.width].filter(Boolean).join(" × "),
            comparisonValues: [data.techSpecs.length, data.techSpecs.width].filter(Boolean),
            comparisonKeys: ["length", "width"].filter((_, index) => [data.techSpecs.length, data.techSpecs.width][index]),
        },
    ].filter(Boolean) : [];
    const categorizedSpecs = mergeCategorizedSpecs(data.techSpecs?.detailedSpecs || [], summarySpecs, t);
    const allDetailedSpecsExpanded = categorizedSpecs.length > 0
        && expandedGroups.length === categorizedSpecs.length;
    const detailedSpecPanelIds = categorizedSpecs.map((_, index) => `tech-spec-group-${index}-panel`);

    return (
        <div data-testid="analysis-report">
            <PageHeader
                eyebrow={
                    shared
                        ? t("report.eyebrowShared")
                        : t("report.eyebrowId", { id: data.id?.slice(0, 8) })
                }
                title={`${data.make} ${data.model}`}
                description={`${data.year} · ${fmtMileage(data.mileage, data.mileageUnit)} · ${data.fuelType} · ${data.transmission}`}
                actions={
                    <>
                        {!shared && (
                            <Btn
                                variant="outline"
                                icon={ArrowLeft}
                                onClick={() => navigate(-1)}
                                testid="back-to-dashboard"
                            >
                                {t("report.back")}
                            </Btn>
                        )}
                        <Btn
                            variant="outline"
                            icon={ExternalLink}
                            href={data.url}
                            target="_blank"
                            testid="open-listing"
                        >
                            {t("report.openListing")}
                        </Btn>
                    </>
                }
            />

            <div className="px-6 md:px-10 py-6 grid grid-cols-1 lg:grid-cols-3 gap-[2px] bg-black">
                {/* Score block */}
                <Card testid="report-verdict" className={`lg:col-span-1 p-6 ${
                    data.verdict === "good" || data.verdict === "great" ? "bg-[#34C759]/10" :
                    data.verdict === "fair" || data.verdict === "average" ? "bg-[#FFCC00]/10" :
                    "bg-[#FF3B30]/10"
                }`}>
                    <Eyebrow className="text-zinc-500">{t("report.sectionVerdict")}</Eyebrow>
                    <div className="mt-3 flex items-center gap-3">
                        <VerdictBadge verdict={data.verdict} size="lg" />
                    </div>
                    <div className="mt-8 flex items-baseline gap-3">
                        <span
                            data-testid="report-score"
                            className="font-display text-7xl leading-none"
                            style={{ color: verdictColor(data.verdict) }}
                        >
                            {data.score}
                        </span>
                        <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
                            / 100
                        </span>
                    </div>
                    <p data-testid="report-summary" className="mt-4 text-sm leading-relaxed text-zinc-600">
                        {data.summary}
                    </p>
                </Card>

                {/* Summary */}
                <Card className="lg:col-span-2 p-6">
                    <Eyebrow>{t("report.sectionSummary")}</Eyebrow>
                    <div className="mt-4 grid grid-cols-4 gap-6">
                        <Stat label={t("report.statAsking")} value={fmtCurrency(data.price, data.currency)} />
                        <Stat label={t("report.statMarket")} value={mp ? `${mp.percentageVsMarket > 0 ? "+" : ""}${mp.percentageVsMarket}%` : "—"} accent={mp?.assessment === "below" ? "good" : mp?.assessment === "above" ? "avoid" : null} />
                        <Stat label={t("report.statMileage")} value={fmtMileage(data.mileage, data.mileageUnit)} />
                        <Stat label={t("report.statYear")} value={data.year} />
                    </div>

                    {!shared && (
                        <div className="mt-6 flex flex-wrap items-end gap-4 pt-4 border-t-2 border-black">
                            <div className="flex-1 min-w-[200px]">
                                <Eyebrow>{t("report.sectionCollection")}</Eyebrow>
                                <div className="mt-1">
                                    <Select value={data.collectionId || ""} onValueChange={changeCollection}>
                                        <SelectTrigger data-testid="collection-select" className="w-full border-2 border-black bg-white dark:bg-zinc-900 px-3 py-2 font-mono text-xs uppercase tracking-[0.15em]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {collections.map((c) => (
                                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <Btn
                                onClick={() => {
                                    const prev = sessionStorage.getItem("spotcar-compare-ids");
                                    const ids = prev ? prev.split(",").filter(Boolean) : [];
                                    const next = Array.from(new Set([...ids, data.id]));
                                    sessionStorage.setItem("spotcar-compare-ids", next.join(","));
                                }}
                                href="/app/compare"
                                variant="outline"
                                icon={Folder}
                            >
                                {t("report.addToCompare")}
                            </Btn>
                        </div>
                    )}
                </Card>

                {/* Pros / Cons — merged single card */}
                <Card className="lg:col-span-2 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:border-r md:border-zinc-200 md:pr-6">
                            <Eyebrow>{t("report.pros")}</Eyebrow>
                            <ul className="mt-4 space-y-2" data-testid="report-pros">
                                {(data.pros || []).map((p, i) => (
                                    <li key={i} className="flex gap-2 text-sm">
                                        <span className="font-mono text-[#34C759] mt-0.5">+</span>
                                        <span>{p}</span>
                                    </li>
                                ))}
                            </ul>
                            {data.greenFlags?.length > 0 && (
                                <>
                                    <Eyebrow className="mt-6 block">{t("report.greenFlags")}</Eyebrow>
                                    <ul className="mt-3 space-y-2">
                                        {data.greenFlags.map((p, i) => (
                                            <li key={i} className="text-xs text-zinc-700">
                                                · {p}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                        <div>
                            <Eyebrow>{t("report.cons")}</Eyebrow>
                            <ul className="mt-4 space-y-2" data-testid="report-cons">
                                {(data.cons || []).map((p, i) => (
                                    <li key={i} className="flex gap-2 text-sm">
                                        <span className="font-mono text-[#FF3B30] mt-0.5">−</span>
                                        <span>{p}</span>
                                    </li>
                                ))}
                            </ul>
                            {data.redFlags?.length > 0 && (
                                <>
                                    <Eyebrow className="mt-6 block">{t("report.redFlags")}</Eyebrow>
                                    <ul className="mt-3 space-y-2">
                                        {data.redFlags.map((p, i) => (
                                            <li
                                                key={i}
                                                className="text-xs text-[#FF3B30] font-medium"
                                            >
                                                · {p}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Model reliability */}
                <Card testid="report-model-insight" className="lg:col-span-1 p-6">
                    <Eyebrow>{t("report.reliability")}</Eyebrow>
                    <p
                        className="mt-3 text-sm leading-relaxed"
                        data-testid="model-insight"
                    >
                        {data.modelInsight}
                    </p>
                    {data.mileageNote && (
                        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">
                            ── {data.mileageNote}
                        </p>
                    )}
                </Card>

                {/* Technical Specifications */}
                {shouldRenderTechSpecs && (
                    <Card testid="report-tech-specs" className="lg:col-span-3 p-6">
                        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                                <Eyebrow as="h2" className="text-zinc-600 dark:text-zinc-400">
                                    {t("report.techSpecs")}
                                </Eyebrow>
                                {data.techSpecs.matchConfidence !== "shared" && data.techSpecs.variantName && (
                                    <p data-testid="spec-variant" className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">
                                        {data.techSpecs.variantName}
                                    </p>
                                )}
                            </div>
                            {categorizedSpecs.length > 0 && (
                                <button
                                    type="button"
                                    className="min-h-11 shrink-0 px-3 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:bg-[var(--surface-container-highest)] hover:text-zinc-800 dark:hover:text-zinc-300 transition-colors motion-reduce:transition-none focus-visible:bg-[var(--surface-container-highest)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--primary)]"
                                    onClick={() => {
                                        setExpandedGroups(allDetailedSpecsExpanded ? [] : categorizedSpecs.map((_, i) => i));
                                    }}
                                    aria-expanded={allDetailedSpecsExpanded}
                                    aria-controls={detailedSpecPanelIds.join(" ")}
                                    data-testid="expand-all-specs"
                                >
                                    {allDetailedSpecsExpanded ? t("report.collapseAll") : t("report.expandAll")}
                                </button>
                            )}
                        </div>
                        {(isConvertedSpecs || hasMatchCaveat) && (
                            <aside
                                className="mt-4 border-l-4 border-[#FFCC00] bg-zinc-100 px-4 py-3 dark:bg-zinc-800"
                                aria-labelledby={isConvertedSpecs ? "tech-specs-notice-title" : undefined}
                                data-testid="tech-specs-notice"
                            >
                                {isConvertedSpecs && (
                                    <div>
                                        <h3 id="tech-specs-notice-title" className="text-sm font-semibold">
                                            {t("report.convertedSpecsTitle")}
                                        </h3>
                                        <p className="mt-1 text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
                                            {t("report.convertedSpecsNotice", {
                                                factoryFuelType: data.techSpecs.factoryFuelType,
                                                listingFuelType: data.techSpecs.listingFuelType,
                                            })}
                                        </p>
                                    </div>
                                )}
                                {data.techSpecs.matchConfidence === "inferred" && (
                                    <p className={`${isConvertedSpecs ? "mt-2 " : ""}text-xs leading-relaxed text-zinc-600 dark:text-zinc-400`}>
                                        {t("report.techSpecsMatchInferred")}
                                    </p>
                                )}
                            </aside>
                        )}
                        {categorizedSpecs.length > 0 && (
                            <div className="mt-4 grid grid-cols-1 items-start gap-3 md:grid-cols-2" data-testid="spec-groups-grid">
                                {categorizedSpecs.map((group, groupIndex) => {
                                    const isExpanded = expandedGroups.includes(groupIndex);
                                    const panelId = detailedSpecPanelIds[groupIndex];
                                    const buttonId = `tech-spec-group-${groupIndex}-button`;

                                    return (
                                        <div key={groupIndex} className="overflow-hidden border-2 border-black dark:border-zinc-700">
                                            <button
                                                id={buttonId}
                                                type="button"
                                                className="flex min-h-11 w-full items-center justify-between gap-3 bg-zinc-100 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-left hover:bg-[var(--surface-container-highest)] dark:bg-zinc-800 dark:hover:bg-zinc-700 transition-colors motion-reduce:transition-none focus-visible:bg-[var(--surface-container-highest)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--primary)]"
                                                onClick={() => setExpandedGroups(prev =>
                                                    isExpanded ? prev.filter(i => i !== groupIndex) : [...prev, groupIndex]
                                                )}
                                                aria-expanded={isExpanded}
                                                aria-controls={panelId}
                                                data-testid={`spec-group-${groupIndex}`}
                                            >
                                                <span data-testid={`spec-group-${groupIndex}-category`} className="min-w-0 break-words">{group.category}</span>
                                                <svg aria-hidden="true" className={`h-3 w-3 shrink-0 transition-transform motion-reduce:transition-none ${isExpanded ? "rotate-180" : ""}`} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 5l3 3 3-3" /></svg>
                                            </button>
                                            <div
                                                id={panelId}
                                                role="region"
                                                aria-labelledby={buttonId}
                                                aria-hidden={!isExpanded}
                                                hidden={!isExpanded}
                                                data-testid={`spec-group-${groupIndex}-panel`}
                                                className="grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none"
                                                style={{ gridTemplateRows: isExpanded ? "1fr" : "0fr" }}
                                            >
                                                <div className="overflow-hidden">
                                                    <dl className="grid grid-cols-2 gap-px bg-black dark:bg-zinc-700">
                                                        {group.items.map((item, itemIndex) => (
                                                            <SpecItem
                                                                key={itemIndex}
                                                                label={item.label}
                                                                value={item.value}
                                                                testid={`spec-group-${groupIndex}-item-${itemIndex}`}
                                                            />
                                                        ))}
                                                        {group.items.length % 2 !== 0 && (
                                                            <div className="bg-white dark:bg-zinc-900 p-3" />
                                                        )}
                                                    </dl>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Card>
                )}

                {/* Safety data */}
                {showSafety && <Card testid="report-safety" className="lg:col-span-3 p-6">
                    <Eyebrow>{t("report.safetyRecalls")}</Eyebrow>
                    <div className="mt-4 flex gap-px bg-black">
                        <MiniTab
                            testid="safety-recalls"
                            label={t("report.statRecalls")}
                            value={data.nhtsaRecalls ?? 0}
                            active={safetyTab === "recalls"}
                            onClick={() => setSafetyTab(safetyTab === "recalls" ? null : "recalls")}
                            hasContent={data.nhtsaRecallSummaries?.length > 0}
                        />
                        <MiniTab
                            testid="safety-complaints"
                            label={t("report.statComplaints")}
                            value={data.nhtsaComplaints ?? 0}
                            active={safetyTab === "complaints"}
                            onClick={() => setSafetyTab(safetyTab === "complaints" ? null : "complaints")}
                            hasContent={data.nhtsaComponentBreakdown && Object.keys(data.nhtsaComponentBreakdown).length > 0}
                        />
                        {(data.nhtsaCrashes ?? 0) > 0 && <Mini
                            label={t("report.statCrashes")}
                            value={data.nhtsaCrashes}
                        />}
                    </div>
                    {safetyTab === "complaints" && data.nhtsaComponentBreakdown && Object.keys(data.nhtsaComponentBreakdown).length > 0 && (
                        <div className="mt-4">
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(data.nhtsaComponentBreakdown)
                                    .sort(([, a], [, b]) => b - a)
                                    .map(([component, count]) => (
                                        <span
                                            key={component}
                                            className="border border-zinc-300 dark:border-zinc-600 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.1em]"
                                        >
                                            {component.toLowerCase()} <span className="text-zinc-400">({count})</span>
                                        </span>
                                    ))}
                            </div>
                            {data.nhtsaComplaintDetails?.length > 0 && (
                                <ol className="mt-4 space-y-3">
                                    {data.nhtsaComplaintDetails.map((c, i) => (
                                        <li key={i} className="flex gap-3 text-xs">
                                            <span className="font-display text-2xl text-zinc-300 leading-none">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <div className="leading-relaxed">
                                                <div className="flex flex-wrap gap-1.5 mb-2">
                                                    {c.component?.split(",").map((comp, ci) => (
                                                        <span key={ci} className="font-mono text-[10px] uppercase tracking-[0.1em] bg-zinc-800 dark:bg-zinc-700 text-zinc-100 px-2 py-0.5 rounded-sm">
                                                            {comp.trim().toLowerCase()}
                                                        </span>
                                                    ))}
                                                    {c.hasCrash && <span className="text-[10px] bg-red-900/40 text-red-300 px-2 py-0.5 rounded-sm">💥 crash</span>}
                                                    {c.hasFire && <span className="text-[10px] bg-orange-900/40 text-orange-300 px-2 py-0.5 rounded-sm">🔥 fire</span>}
                                                </div>
                                                <span className="whitespace-pre-line">{c.summary?.replace(/[-_]{10,}/g, "\n")}</span>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </div>
                    )}
                    {safetyTab === "recalls" && data.nhtsaRecallSummaries?.length > 0 && (
                        <ol className="mt-4 space-y-3">
                            {data.nhtsaRecallSummaries.map((s, i) => (
                                <li key={i} className="flex gap-3 text-xs">
                                    <span className="font-display text-2xl text-zinc-300 leading-none">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="leading-relaxed">{s}</span>
                                </li>
                            ))}
                        </ol>
                    )}
                </Card>}

                {/* Odometer */}
                {showOdometer && <Card testid="report-odometer" className="lg:col-span-3 p-6">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <div>
                            <Eyebrow>{t("report.odometerCheck")}</Eyebrow>
                            <div className="font-display text-xl uppercase mt-2">
                                {data.odometerVehicleName || t("report.vinHistory")}
                            </div>
                        </div>
                        {data.odometerFraudDetected ? (
                            <span className="border-2 border-black bg-[#FF3B30] text-white px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em]">
                                {t("report.fraudFlagged")}
                            </span>
                        ) : (
                            <span className="border-2 border-black bg-[#34C759] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em]">
                                {t("report.clean")}
                            </span>
                        )}
                    </div>
                    {data.odometerFraudDetails && (
                        <div className="mt-3 border-2 border-[#FF3B30] bg-[#FFEDED] p-3 font-mono text-xs leading-relaxed">
                            {data.odometerFraudDetails}
                        </div>
                    )}
                    {data.odometerRecords?.length > 0 && (
                        <table className="w-full mt-4 text-xs">
                            <thead className="border-b-2 border-black font-mono uppercase tracking-[0.2em] text-[10px] text-zinc-500">
                                <tr>
                                    <th className="text-left p-2">{t("report.odometerDate")}</th>
                                    <th className="text-left p-2">{t("report.odometerType")}</th>
                                    <th className="text-right p-2">{t("report.odometerMileage")}</th>
                                    <th className="text-left p-2">{t("report.odometerResult")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.odometerRecords.map((r, i) => (
                                    <tr
                                        key={i}
                                        data-testid={`odometer-record-${i}`}
                                        className="border-b border-zinc-200 last:border-b-0"
                                    >
                                        <td className="p-2 font-mono" data-testid="odometer-date">
                                            {fmtDate(r.date)}
                                        </td>
                                        <td className="p-2" data-testid="odometer-type">{r.type}</td>
                                        <td className="p-2 text-right font-mono" data-testid="odometer-mileage">
                                            {fmtMileage(r.mileage)}
                                        </td>
                                        <td className="p-2 font-mono uppercase text-[10px]" data-testid="odometer-result">
                                            {r.result}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Card>}

                {/* Maintenance forecast */}
                <Card testid="report-maintenance" className="lg:col-span-3 p-6">
                    <Eyebrow>{t("report.maintenance")}</Eyebrow>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {maint.map((m, i) => (
                            <div
                                key={i}
                                data-testid={`maint-${i}`}
                                className="border-2 border-black p-4"
                            >
                                <div
                                    className={`inline-block border-2 border-black px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] mb-2 ${m.urgency === "upcoming" ? "bg-[#FFCC00]" : m.urgency === "routine" ? "bg-white" : "bg-zinc-100"}`}
                                >
                                    {m.urgency}
                                </div>
                                <div className="font-display text-base uppercase leading-tight">
                                    {m.item.split("/").reduce((acc, part, i, arr) =>
                                        i < arr.length - 1
                                            ? [...acc, part, <wbr key={i} />, "/"]
                                            : [...acc, part]
                                    , [])}
                                </div>
                                <div className="mt-2 font-mono text-xs">
                                    {m.estimatedCost}
                                </div>
                                <div className="font-mono text-[10px] text-zinc-500 mt-1">
                                    {m.mileageInterval}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Market price */}
                {mp && mp.comparableListingsFound > 0 && (
                    <Card testid="report-market-price" className="lg:col-span-3 p-6 bg-black text-white">
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-start gap-4">
                            <div>
                                <Eyebrow className="text-zinc-400">
                                    {t("report.marketPrice")}
                                </Eyebrow>
                                <div className="font-display text-2xl uppercase mt-2">
                                    {t("report.comparableListings", { count: mp.comparableListingsFound })}
                                </div>
                                <p data-testid="market-explanation" className="mt-1 text-sm text-zinc-300">
                                    {mp.explanation}
                                </p>
                            </div>
                            <div className="md:text-right">
                                <Eyebrow className="text-zinc-400">{t("report.vsMarket")}</Eyebrow>
                                <div
                                    data-testid="market-percentage"
                                    className="font-display text-5xl leading-none mt-1"
                                    style={{
                                        color:
                                            mp.assessment === "below" ||
                                            mp.assessment === "underpriced"
                                                ? "#34C759"
                                                : mp.assessment === "above" ||
                                                    mp.assessment === "overpriced"
                                                  ? "#FF3B30"
                                                  : "#FFCC00",
                                    }}
                                >
                                    {mp.percentageVsMarket > 0 ? "+" : ""}
                                    {mp.percentageVsMarket}%
                                </div>
                                <div data-testid="market-median" className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mt-1">
                                    {t("report.median")} {fmtCurrency(mp.medianMarketPrice, mp.marketCurrency)}
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 flex flex-wrap gap-2">
                            {(mp.referenceSites || []).map((s) => (
                                <span
                                    key={s}
                                    className="border border-white/40 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em]"
                                >
                                    {s}
                                </span>
                            ))}
                        </div>
                    </Card>
                )}

                {/* Negotiation + Inspection — 50/50 */}
                <div className="lg:col-span-3 grid grid-cols-1 lg:grid-cols-2 gap-[2px] bg-black">
                {/* Negotiation */}
                <Card testid="report-negotiation" className="p-6">
                    <div className="flex items-center justify-between">
                        <Eyebrow>{t("report.negotiation")}</Eyebrow>
                        <Btn
                            size="sm"
                            variant="outline"
                            icon={Copy}
                            onClick={() =>
                                copyList(t("report.negotiation"), r.negotiationTips || [])
                            }
                            testid="copy-negotiation"
                        >
                            {t("report.copyAll")}
                        </Btn>
                    </div>
                    <ol className="mt-4 space-y-3" data-testid="negotiation-list">
                        {(r.negotiationTips || []).map((t, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm">
                                <span className="font-display text-xl text-zinc-300 leading-[1.4]">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span className="leading-relaxed">{t}</span>
                            </li>
                        ))}
                    </ol>
                </Card>

                {/* Inspection checklist */}
                <Card testid="report-inspection" className="p-6">
                    <div className="flex items-center justify-between">
                        <Eyebrow>{t("report.inspection")}</Eyebrow>
                        <Btn
                            size="sm"
                            variant="outline"
                            icon={Copy}
                            onClick={() =>
                                copyList(
                                    t("report.inspection"),
                                    r.inspectionChecklist || [],
                                )
                            }
                            testid="copy-inspection"
                        >
                            {t("report.copyAll")}
                        </Btn>
                    </div>
                    <ul
                        className="mt-4 space-y-2"
                        data-testid="inspection-list"
                    >
                        {(r.inspectionChecklist || []).map((c, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-2 text-sm leading-relaxed"
                            >
                                <span className="h-3.5 w-3.5 border border-current inline-block flex-shrink-0 mt-[3px]" />
                                <span>{c}</span>
                            </li>
                        ))}
                    </ul>
                </Card>
                </div>

                {/* Personal notes */}
                {!shared && (
                    <Card testid="personal-notes-card" className="lg:col-span-3 p-6">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <Eyebrow>{t("report.personalNotes")}</Eyebrow>
                                {!editingNote && data.notes && (
                                    <button
                                        onClick={() => {
                                            setEditingNote(true);
                                            setTimeout(() => noteRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
                                        }}
                                        className="text-zinc-400 hover:text-zinc-700 p-2"
                                        data-testid="edit-note"
                                        title={t("report.editNote")}
                                        aria-label={t("report.editNote")}
                                    >
                                        <Edit3 size={14} />
                                    </button>
                                )}
                            </div>
                            {editingNote && (
                                <div className="flex gap-2">
                                    <Btn
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                            setEditingNote(false);
                                            setNoteDraft(data.notes || "");
                                        }}
                                    >
                                        {t("report.cancelNote")}
                                    </Btn>
                                    <Btn
                                        size="sm"
                                        variant="primary"
                                        icon={Save}
                                        onClick={saveNote}
                                        testid="save-note"
                                    >
                                        {t("report.saveNote")}
                                    </Btn>
                                </div>
                            )}
                        </div>
                        {editingNote ? (
                            <textarea
                                ref={noteRef}
                                className="w-full mt-4 border-2 border-black bg-white p-3 font-mono text-sm focus:outline-none min-h-[100px]"
                                value={noteDraft}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Escape") {
                                        setEditingNote(false);
                                        setNoteDraft(data.notes || "");
                                    }
                                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                                        saveNote();
                                    }
                                }}
                                placeholder={t("report.notePlaceholder")}
                                data-testid="note-textarea"
                            />
                        ) : data.notes ? (
                            <p className="ph-mask mt-4 text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap">
                                {data.notes}
                            </p>
                        ) : (
                            <button
                                onClick={() => {
                                    setEditingNote(true);
                                    setTimeout(() => noteRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
                                }}
                                className="mt-4 text-sm italic text-zinc-400 hover:text-zinc-600 transition-colors cursor-pointer"
                                data-testid="add-note"
                            >
                                {t("report.noNotesClickToAdd")}
                            </button>
                        )}
                    </Card>
                )}
            </div>
        </div>
    );
}

function normalizeSpecValue(value) {
    return String(value ?? "")
        .normalize("NFKC")
        .toLocaleLowerCase()
    .replace(/[×✕✖]/g, "x")
    .replace(/[‐‑‒–—−]/g, "-")
    .replace(/(\d)[,\u00a0\u202f ](?=\d{3}(?:\D|$))/g, "$1")
    .replace(/\s+/g, " ")
    .replace(/\s*([x/\-])\s*/g, "$1")
    .trim();
}

const SPEC_FIELD_TRANSLATION_KEYS = {
    co2Emissions: "report.specCo2",
    emissionsStandard: "report.specEmissionsStandard",
    fuelConsumption: "report.specFuelConsumption",
    performance: "report.specPerformance",
    acceleration: "report.specAcceleration",
    topSpeed: "report.specTopSpeed",
    engineDisplacement: "report.specEngineDisplacement",
    cylinders: "report.specCylinders",
    torque: "report.specTorque",
    power: "report.specPower",
    gearbox: "report.specGearbox",
    drivetrain: "report.specDrivetrain",
    doors: "report.specDoors",
    bodyType: "report.specBodyType",
    curbWeight: "report.specWeight",
    grossWeight: "report.specGrossWeight",
    trunkVolume: "report.specTrunk",
    length: "report.specLength",
    width: "report.specWidth",
};

const SPEC_FIELD_ALIASES = [
    ["co2Emissions", ["co2 emissions", "co₂ emissions", "carbon dioxide emissions", "co2", "co₂"]],
    ["emissionsStandard", ["emissions standard", "emission standard"]],
    ["fuelConsumption", ["fuel consumption and economy", "fuel consumption", "combined consumption", "fuel economy"]],
    ["performance", ["performance"]],
    ["acceleration", ["acceleration 0-100 km/h", "acceleration", "0-100 km/h", "0 to 100 km/h"]],
    ["topSpeed", ["top speed", "maximum speed"]],
    ["engineDisplacement", ["engine displacement", "displacement", "engine capacity"]],
    ["cylinders", ["number of cylinders", "cylinder count", "cylinders"]],
    ["torque", ["maximum torque", "torque"]],
    ["power", ["power output", "engine power", "horsepower", "power"]],
    ["gearbox", ["number of gears and the gearbox type", "number of gears and gearbox type", "gearbox type", "gearbox", "transmission"]],
    ["drivetrain", ["drivetrain", "drive type", "driven wheels", "drive wheels"]],
    ["doors", ["number of doors", "door count", "doors"]],
    ["bodyType", ["body type", "body style"]],
    ["curbWeight", ["curb weight", "kerb weight", "curb mass", "kerb mass"]],
    ["grossWeight", ["maximum permissible mass", "gross weight", "maximum weight", "gross mass", "maximum mass"]],
    ["trunkVolume", ["trunk (boot) capacity", "trunk volume", "trunk capacity", "boot volume", "boot capacity", "cargo volume", "luggage capacity"]],
    ["length", ["body length", "length"]],
    ["width", ["body width", "width"]],
];

function getSemanticSpecField(label, t) {
    const normalizedLabel = normalizeSpecValue(label);

    if (normalizedLabel.startsWith("how environmentally friendly is ")) {
        return { field: "emissionsStandard", useCanonicalLabel: true };
    }
    if (normalizedLabel.startsWith("how fast is ")) {
        return { field: "performance", useCanonicalLabel: true };
    }

    const translatedField = Object.entries(SPEC_FIELD_TRANSLATION_KEYS).find(([, key]) => (
        normalizeSpecValue(t(key)) === normalizedLabel
    ));
    if (translatedField) {
        return { field: translatedField[0], useCanonicalLabel: true };
    }

    for (const [field, aliases] of SPEC_FIELD_ALIASES) {
        for (const alias of aliases) {
            const normalizedAlias = normalizeSpecValue(alias);

            if (normalizedLabel === normalizedAlias) {
                return { field, useCanonicalLabel: true };
            }

            // Provider headings append a vehicle description after the field name.
            // Only these explicit separators are stripped so qualifiers and unknown
            // labels remain intact, and model suffix words cannot drive classification.
            if (normalizedLabel.startsWith(`${normalizedAlias} of `) || normalizedLabel.startsWith(`${normalizedAlias} in `)) {
                return { field, useCanonicalLabel: true };
            }

            if (normalizedLabel.startsWith(`${normalizedAlias} (`)) {
                return { field, useCanonicalLabel: false };
            }
        }
    }

    return { field: normalizedLabel, useCanonicalLabel: false };
}

function getCanonicalSpecField(label, t) {
    return getSemanticSpecField(label, t).field;
}

function getSpecDisplayLabel(label, t) {
    const semanticField = getSemanticSpecField(label, t);
    return semanticField.useCanonicalLabel
        ? t(SPEC_FIELD_TRANSLATION_KEYS[semanticField.field])
        : label;
}

function getUnrepresentedSpec(spec, representedSpecs) {
    const comparisonValues = spec.comparisonValues || [spec.value];
    const comparisonKeys = spec.comparisonKeys || [spec.key];
    const missingComponents = comparisonValues
        .map((value, index) => ({
            value,
            label: spec.comparisonLabels?.[index],
            key: comparisonKeys[index] || spec.key,
        }))
        .filter(({ value, key }) => !representedSpecs.has(
            `${spec.category}|${key}|${normalizeSpecValue(value)}`,
        ));

    if (missingComponents.length === 0) return null;
    if (missingComponents.length === comparisonValues.length) return spec;

    return {
        ...spec,
        label: missingComponents.length === 1 && missingComponents[0].label
            ? missingComponents[0].label
            : spec.label,
        value: missingComponents.map(({ value }) => value).join(" · "),
        comparisonValues: missingComponents.map(({ value }) => value),
        comparisonKeys: missingComponents.map(({ key }) => key),
    };
}

const SPEC_CATEGORY_ORDER = [
    "general",
    "engine",
    "fuelEmissions",
    "performance",
    "powertrain",
    "dimensions",
    "weightCapacity",
];

function getSpecCategory(category, t) {
    const normalizedCategory = normalizeSpecValue(category);

    const translatedCategory = SPEC_CATEGORY_ORDER.find((candidate) => (
        normalizeSpecValue(getSpecCategoryLabel(candidate, t)) === normalizedCategory
    ));
    if (translatedCategory) return translatedCategory;

    if (/\b(powertrain|drivetrain|gearbox|transmission)\b/.test(normalizedCategory)) return "powertrain";
    if (/\b(weight|capacity|trunk|cargo)\b/.test(normalizedCategory)) return "weightCapacity";
    if (/\b(fuel|emission|consumption)\b/.test(normalizedCategory)) return "fuelEmissions";
    if (/\b(performance|acceleration|speed)\b/.test(normalizedCategory)) return "performance";
    if (/\b(engine|motor)\b/.test(normalizedCategory)) return "engine";
    if (/\b(dimension|dimensions|measurement|size)\b/.test(normalizedCategory)) return "dimensions";
    if (/\b(general|body)\b/.test(normalizedCategory)) return "general";

    return null;
}

function getSpecCategoryLabel(category, t) {
    const keys = {
        general: "report.specCategoryGeneral",
        engine: "report.specCategoryEngine",
        fuelEmissions: "report.specCategoryFuelEmissions",
        performance: "report.specCategoryPerformance",
        powertrain: "report.specCategoryPowertrain",
        dimensions: "report.specCategoryDimensions",
        weightCapacity: "report.specCategoryWeightCapacity",
    };

    return t(keys[category]);
}

function mergeCategorizedSpecs(detailedSpecs, summarySpecs, t) {
    const groups = [];

    detailedSpecs.forEach((group) => {
        const semanticCategory = getSpecCategory(group.category, t);
        const canonicalGroup = semanticCategory
            ? groups.find((existingGroup) => existingGroup.semanticCategory === semanticCategory)
            : null;
        const items = [...(group.items || [])];

        if (canonicalGroup) {
            canonicalGroup.items.push(...items);
            return;
        }

        groups.push({
            ...group,
            category: semanticCategory ? getSpecCategoryLabel(semanticCategory, t) : group.category,
            semanticCategory,
            items,
        });
    });

    const representedSpecs = new Set();
    groups.forEach((group) => {
        const groupSpecs = new Set();
        const uniqueItems = [];

        group.items.forEach((item) => {
            const field = getCanonicalSpecField(item.label, t);
            const identity = `${group.semanticCategory || normalizeSpecValue(group.category)}|${field}|${normalizeSpecValue(item.value)}`;
            if (groupSpecs.has(identity)) return;

            groupSpecs.add(identity);
            representedSpecs.add(identity);
            uniqueItems.push({
                ...item,
                label: getSpecDisplayLabel(item.label, t),
            });
        });

        group.items = uniqueItems;
    });

    SPEC_CATEGORY_ORDER.forEach((category) => {
        const fallbackItems = summarySpecs
            .filter((spec) => spec.category === category)
            .map((spec) => getUnrepresentedSpec(spec, representedSpecs))
            .filter(Boolean)
            .filter((spec) => {
                const comparisonValues = spec.comparisonValues || [spec.value];
                const comparisonKeys = spec.comparisonKeys || [spec.key];
                comparisonValues.forEach((value, index) => {
                    representedSpecs.add(`${category}|${comparisonKeys[index] || spec.key}|${normalizeSpecValue(value)}`);
                });
                return true;
            });

        if (fallbackItems.length === 0) return;

        const existingGroup = groups.find((group) => group.semanticCategory === category);
        if (existingGroup) {
            existingGroup.items.push(...fallbackItems);
            return;
        }

        groups.push({
            category: getSpecCategoryLabel(category, t),
            semanticCategory: category,
            items: fallbackItems,
        });
    });

    return groups.sort((left, right) => {
        const leftIndex = SPEC_CATEGORY_ORDER.indexOf(left.semanticCategory);
        const rightIndex = SPEC_CATEGORY_ORDER.indexOf(right.semanticCategory);
        const leftOrder = leftIndex === -1 ? SPEC_CATEGORY_ORDER.length : leftIndex;
        const rightOrder = rightIndex === -1 ? SPEC_CATEGORY_ORDER.length : rightIndex;
        return leftOrder - rightOrder;
    });
}

function SpecItem({ label, value, testid }) {
    return (
        <div data-testid={testid} className="bg-white dark:bg-zinc-900 p-3">
            <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {label}
            </dt>
            <dd className="text-sm font-medium leading-snug mt-1">
                {value}
            </dd>
        </div>
    );
}

function Stat({ label, value, accent }) {
    const color =
        accent === "good"
            ? "text-[#34C759]"
            : accent === "avoid"
              ? "text-[#FF3B30]"
              : "";
    return (
        <div className="min-w-0">
            <Eyebrow>{label}</Eyebrow>
            <div className={`font-display text-base lg:text-xl mt-1 leading-snug ${color}`}>
                {value}
            </div>
        </div>
    );
}

function MiniTab({ label, value, active, onClick, hasContent, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            className={`flex-1 bg-white dark:bg-zinc-900 p-3 text-left transition-colors ${hasContent ? "cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800" : "cursor-default"}`}
            style={{ borderBottom: active ? "4px solid #FFCC00" : undefined }}
            onClick={hasContent ? onClick : undefined}
        >
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {label}
            </div>
            <div data-testid={testid ? `${testid}-value` : undefined} className="font-display text-2xl leading-none mt-1">
                {value}
            </div>
        </button>
    );
}

function Mini({ label, value, accent }) {
    return (
        <div
            className="flex-1 bg-white dark:bg-zinc-900 p-3"
        >
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {label}
            </div>
            <div className="font-display text-2xl leading-none mt-1">
                {value}
            </div>
        </div>
    );
}
